import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import { Textarea } from "@/components/ui/textarea";

const meetingSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  start_at: z.string().min(1, "Start time is required"),
  end_at: z.string().optional(),
  transcript_url: z.string().url("Invalid URL").optional().or(z.literal("")),
});

type MeetingFormData = z.infer<typeof meetingSchema>;

interface CreateMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateMeetingDialog({ open, onOpenChange, onSuccess }: CreateMeetingDialogProps) {
  const { toast } = useToast();
  const { orgId, familyId } = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<MeetingFormData>({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      title: "",
      start_at: "",
      end_at: "",
      transcript_url: "",
    },
  });

  const onSubmit = async (data: MeetingFormData) => {
    setIsSubmitting(true);
    try {
      if (!orgId) {
        throw new Error("Organization not loaded yet. Please try again.");
      }

      // Get the first client for this org/family
      const { data: clients } = await supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId)
        .limit(1)
        .single();

      if (!clients) {
        throw new Error("No client record found for this organization");
      }

      const { error } = await supabase.from("meetings").insert({
        title: data.title,
        start_at: data.start_at,
        end_at: data.end_at || null,
        transcript_url: data.transcript_url || null,
        client_id: clients.id,
        org_id: orgId,
        family_id: familyId,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Meeting created successfully",
      });

      form.reset();
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error("Error creating meeting:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to create meeting",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Meeting</DialogTitle>
          <DialogDescription>
            Create a new meeting record
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="Q4 Portfolio Review" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="start_at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date & Time *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date & Time</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="transcript_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transcript URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://example.com/transcript" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Meeting"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
